import {
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  Edit as EditIcon,
  PersonOutline as PersonIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import LoadingButton from "../../components/LoadingButton";
import { hostname, parseError } from "../../utils";

interface User {
  id: string;
  email: string;
  displayName: string | null;
  photo: string | null;
  superUser: boolean;
  subscriptionType: string | null;
  creditLimit: number;
  creditUsage: number;
  companyLimit: number;
  whitelabelLimit: number;
  domainLimit: number;
  seats?: number;
  topupCredits?: number;
  lastLogin: string | null;
  createdDate: string | null;
}

interface UserManagementProps {
  getToken: () => Promise<string>;
}

const UserManagement: React.FC<UserManagementProps> = ({ getToken }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Search and filters
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [subscriptionFilter, setSubscriptionFilter] = useState("");

  // Copy UID feedback
  const [copiedUid, setCopiedUid] = useState<string | null>(null);

  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    displayName: "",
    companyLimit: 3,
    whitelabelLimit: 0,
    domainLimit: 3,
    creditLimit: 0,
    subscriptionType: "",
    seats: 0,
    topupCredits: 0,
  });

  const fetchUsers = async (search?: string, subscription?: string) => {
    setLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const params: any = {
        page: page + 1,
        perPage: rowsPerPage,
      };

      if (search) {
        params.search = search;
      }

      if (subscription) {
        params.subscription = subscription;
      }

      const response = await axios.get(`${hostname}/users/admin`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      setUsers(response.data.users);
      setTotalCount(response.data.pagination.total);
    } catch (err) {
      setError(parseError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(searchQuery, subscriptionFilter);
  }, [page, rowsPerPage, subscriptionFilter]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);

    // Debounce search
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      setPage(0);
      fetchUsers(value, subscriptionFilter);
    }, 300);

    setSearchTimeout(timeout);
  };

  const handleSubscriptionFilterChange = (value: string) => {
    setSubscriptionFilter(value);
    setPage(0);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setEditForm({
      displayName: user.displayName || "",
      companyLimit: user.companyLimit,
      whitelabelLimit: user.whitelabelLimit,
      domainLimit: user.domainLimit || 3,
      creditLimit: user.creditLimit,
      subscriptionType: user.subscriptionType || "",
      seats: user.seats || 0,
      topupCredits: user.topupCredits || 0,
    });
    setEditDialogOpen(true);
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;

    setSaving(true);
    setError(null);

    try {
      const token = await getToken();
      await axios.put(
        `${hostname}/users/admin/${editingUser.id}`,
        editForm,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setSuccess("User updated successfully");
      setEditDialogOpen(false);
      fetchUsers(searchQuery, subscriptionFilter);
    } catch (err) {
      setError(parseError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never";
    return moment(dateString).format("MMM D, YYYY");
  };

  const handleCopyUid = (uid: string) => {
    navigator.clipboard.writeText(uid);
    setCopiedUid(uid);
    setTimeout(() => setCopiedUid(null), 2000);
  };

  const getSubscriptionColor = (type: string | null) => {
    switch (type) {
      case "business":
        return "primary";
      case "entrepreneur":
        return "secondary";
      case "free":
      default:
        return "default";
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
        User Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Search and Filters */}
      <Box sx={{ mb: 3, display: "flex", gap: 2, alignItems: "center" }}>
        <TextField
          placeholder="Search by email, name, user ID, or company ID..."
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          size="small"
          sx={{ width: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Subscription</InputLabel>
          <Select
            value={subscriptionFilter}
            label="Subscription"
            onChange={(e) => handleSubscriptionFilterChange(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="free">Free</MenuItem>
            <MenuItem value="non-free">Non-Free (Paid)</MenuItem>
            <MenuItem value="entrepreneur">Entrepreneur</MenuItem>
            <MenuItem value="business">Business</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Users Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>UID</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Subscription</TableCell>
                <TableCell>Credits</TableCell>
                <TableCell>Limits</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Tooltip
                      title={copiedUid === user.id ? "Copied!" : "Click to copy"}
                      placement="top"
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          cursor: "pointer",
                          "&:hover": { color: "primary.main" },
                        }}
                        onClick={() => handleCopyUid(user.id)}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "monospace",
                            maxWidth: 80,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {user.id}
                        </Typography>
                        <ContentCopyIcon sx={{ fontSize: 14, opacity: 0.6 }} />
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Avatar src={user.photo || undefined}>
                        {user.displayName?.[0] || user.email?.[0] || <PersonIcon />}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {user.displayName || "No name"}
                          {user.superUser && (
                            <Chip
                              label="Super"
                              size="small"
                              color="error"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.email}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.subscriptionType || "free"}
                      size="small"
                      color={getSubscriptionColor(user.subscriptionType)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {user.creditUsage.toLocaleString()} / {user.creditLimit.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" display="block">
                      Companies: {user.companyLimit}
                    </Typography>
                    {user.whitelabelLimit > 0 && (
                      <Typography variant="caption" display="block">
                        Whitelabel: {user.whitelabelLimit}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(user.lastLogin)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(user.createdDate)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleEditUser(user)}
                      title="Edit user"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No users found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}

      {/* Edit User Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            Edit User
            <IconButton onClick={() => setEditDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {editingUser && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {editingUser.email}
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Display Name"
                    value={editForm.displayName}
                    onChange={(e) =>
                      setEditForm({ ...editForm, displayName: e.target.value })
                    }
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Subscription Type"
                    value={editForm.subscriptionType}
                    onChange={(e) =>
                      setEditForm({ ...editForm, subscriptionType: e.target.value })
                    }
                    placeholder="free, entrepreneur, business"
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Seats"
                    type="number"
                    value={editForm.seats}
                    onChange={(e) =>
                      setEditForm({ ...editForm, seats: parseInt(e.target.value) || 0 })
                    }
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Credit Limit"
                    type="number"
                    value={editForm.creditLimit}
                    onChange={(e) =>
                      setEditForm({ ...editForm, creditLimit: parseInt(e.target.value) || 0 })
                    }
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Top-up Credits"
                    type="number"
                    value={editForm.topupCredits}
                    onChange={(e) =>
                      setEditForm({ ...editForm, topupCredits: parseInt(e.target.value) || 0 })
                    }
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Company Limit"
                    type="number"
                    value={editForm.companyLimit}
                    onChange={(e) =>
                      setEditForm({ ...editForm, companyLimit: parseInt(e.target.value) || 0 })
                    }
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Whitelabel Limit"
                    type="number"
                    value={editForm.whitelabelLimit}
                    onChange={(e) =>
                      setEditForm({ ...editForm, whitelabelLimit: parseInt(e.target.value) || 0 })
                    }
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Domain Limit"
                    type="number"
                    value={editForm.domainLimit}
                    onChange={(e) =>
                      setEditForm({ ...editForm, domainLimit: parseInt(e.target.value) || 0 })
                    }
                    helperText="Max custom domains for tracking/landing pages"
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <LoadingButton onClick={() => setEditDialogOpen(false)}>
            Cancel
          </LoadingButton>
          <LoadingButton
            variant="contained"
            onClick={handleSaveUser}
            loading={saving}
          >
            Save Changes
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
